/**
 * Regras de publicação no Instagram, Facebook, TikTok, YouTube e LinkedIn,
 * num lugar só.
 *
 * Vivem em `shared/` porque a TELA precisa delas para avisar antes de a pessoa
 * agendar — descobrir na hora marcada que o reel tem 2 segundos a menos que o
 * mínimo é descobrir tarde demais, quando já não há ninguém olhando. O
 * servidor revalida com as mesmas funções ao gravar.
 *
 * Os números são os limites documentados de cada rede (Meta Graph v25,
 * TikTok Content Posting API v2, YouTube Data API v3, LinkedIn Posts API
 * 202609). Onde a rede é mais permissiva que o nosso armazenamento, vale o
 * nosso teto (`MAX_VIDEO_BYTES`).
 */

export const TODAS_AS_REDES = ["instagram", "facebook", "tiktok", "youtube", "linkedin"] as const;
export type SocialNetwork = (typeof TODAS_AS_REDES)[number];
export const REDES_DA_META = ["instagram", "facebook"] as const;
export type RedeDaMeta = (typeof REDES_DA_META)[number];
/** Redes conectadas por OAuth próprio (tabela `social_accounts`), fora da Meta. */
export const REDES_EXTERNAS = ["tiktok", "youtube", "linkedin"] as const;
export type RedeExterna = (typeof REDES_EXTERNAS)[number];
export const ehRedeExterna = (r: string): r is RedeExterna => (REDES_EXTERNAS as readonly string[]).includes(r);
/** `feed` com várias mídias é carrossel (IG) ou álbum (FB). */
export type SocialFormat = "feed" | "reel" | "story";
export type MediaKind = "image" | "video";
export type TrialGraduation = "MANUAL" | "SS_PERFORMANCE";

export type PostStatus = "draft" | "scheduled" | "publishing" | "published" | "partial" | "failed" | "canceled";
export type TargetStatus = "scheduled" | "publishing" | "published" | "failed" | "canceled";

export type SocialMediaInput = {
  kind: MediaKind;
  mimeType: string;
  sizeBytes: number;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
};

export const IMAGE_MIME_TYPES = ["image/jpeg"] as const;
export const VIDEO_MIME_TYPES = ["video/mp4", "video/quicktime"] as const;

/** Imagem: o Instagram aceita até 8 MB, e só JPEG. */
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
/** Vídeo: teto do nosso armazenamento (o Reel do IG aceita até 1 GB). */
export const MAX_VIDEO_BYTES = 300 * 1024 * 1024;

export const IG_CAPTION_MAX = 2200;
export const IG_MAX_HASHTAGS = 30;
export const FB_MESSAGE_MAX = 63_206;
/** Carrossel do Instagram / álbum do Facebook. */
export const CAROUSEL_MAX_ITEMS = 10;
/** Legenda do vídeo no TikTok (o `title` da API, em runas UTF-16). */
export const TIKTOK_CAPTION_MAX = 2200;
/** Post de fotos do TikTok: até 35 imagens. */
export const TIKTOK_PHOTO_MAX = 35;
export const YOUTUBE_TITLE_MAX = 100;
export const YOUTUBE_DESCRIPTION_MAX = 5000;
/** Short: vídeo vertical ou quadrado de até 3 min. */
export const YOUTUBE_SHORT_MAX_MS = 3 * 60_000;
export const LINKEDIN_COMMENTARY_MAX = 3000;
export const LINKEDIN_MULTI_IMAGE_MAX = 20;
/** Maior número de mídias que alguma rede aceita num post (teto do formulário). */
export const MAX_MEDIA_ITEMS = TIKTOK_PHOTO_MAX;

/**
 * Onde a legenda leva o link de captação. Na hora de publicar, cada destino
 * recebe o SEU link curto (é assim que o clique sabe de qual rede veio).
 */
export const LINK_PLACEHOLDER = "{link}";
/** Tamanho que o link curto ocupa na legenda, para conferir limites antes. */
export const TAMANHO_DO_LINK = 80;

/**
 * Proporção aceita no FEED do Instagram (largura ÷ altura): de 4:5 (retrato)
 * a 1,91:1 (paisagem). Fora disso a Meta recusa o container na hora de
 * publicar. Reel e story aceitam qualquer proporção (o ideal é 9:16) — lá é
 * dica, não erro.
 */
export const IG_FEED_PROPORCAO_MIN = 4 / 5;
export const IG_FEED_PROPORCAO_MAX = 1.91;

/** Quanto antes da hora marcada aceitamos agendar, e até quando. */
export const MIN_SCHEDULE_LEAD_MS = 60_000;
export const MAX_SCHEDULE_AHEAD_MS = 180 * 24 * 60 * 60 * 1000;

export const FORMAT_LABELS: Record<SocialFormat, string> = {
  feed: "Post no feed",
  reel: "Reel",
  story: "Story",
};

export const NETWORK_LABELS: Record<SocialNetwork, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  linkedin: "LinkedIn",
};

/**
 * Formatos que cada rede publica pela API. Story só existe na Meta; no TikTok,
 * YouTube e LinkedIn "Reel" é o vídeo curto vertical (no YouTube, um Short).
 */
export const FORMATOS_POR_REDE: Record<SocialNetwork, SocialFormat[]> = {
  instagram: ["feed", "reel", "story"],
  facebook: ["feed", "reel", "story"],
  tiktok: ["feed", "reel"],
  youtube: ["feed", "reel"],
  linkedin: ["feed", "reel"],
};

/** Onde um link escrito na legenda vira link clicável. */
export const LINK_CLICAVEL_NA_LEGENDA: Record<SocialNetwork, boolean> = {
  instagram: false,
  facebook: true,
  tiktok: false,
  youtube: true,
  linkedin: true,
};

// ─── Opções que só uma rede tem ──────────────────────────────────────────────

/**
 * Quem vê o vídeo no TikTok. As diretrizes do TikTok exigem que a PESSOA
 * escolha (sem valor pré-marcado) entre as opções que a conta dela permite —
 * por isso começa `null` e a validação cobra.
 */
export type TikTokPrivacy = "PUBLIC_TO_EVERYONE" | "MUTUAL_FOLLOW_FRIENDS" | "FOLLOWER_OF_CREATOR" | "SELF_ONLY";
export const TIKTOK_PRIVACY_LABELS: Record<TikTokPrivacy, string> = {
  PUBLIC_TO_EVERYONE: "Todo mundo",
  MUTUAL_FOLLOW_FRIENDS: "Amigos (seguidores que você segue de volta)",
  FOLLOWER_OF_CREATOR: "Seguidores",
  SELF_ONLY: "Só eu",
};

export type TikTokOptions = {
  privacyLevel: TikTokPrivacy | null;
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  /** "Sua marca": promove o próprio negócio. */
  brandOrganic: boolean;
  /** "Conteúdo de marca": parceria paga com terceiro. Não pode ser "Só eu". */
  brandedContent: boolean;
  /** Rótulo de conteúdo gerado por IA. */
  aiGenerated: boolean;
};

export type YouTubePrivacy = "public" | "unlisted" | "private";
export const YOUTUBE_PRIVACY_LABELS: Record<YouTubePrivacy, string> = {
  public: "Público",
  unlisted: "Não listado (só quem tem o link)",
  private: "Privado",
};

export type YouTubeOptions = {
  title: string;
  privacy: YouTubePrivacy;
  /** Declaração obrigatória do YouTube (lei COPPA). Null = ainda não respondeu. */
  madeForKids: boolean | null;
};

export type NetworkOptions = {
  tiktok?: TikTokOptions;
  youtube?: YouTubeOptions;
};

/** Diretrizes do TikTok: nada pré-marcado — a pessoa liga o que quiser. */
export const TIKTOK_OPTIONS_PADRAO: TikTokOptions = {
  privacyLevel: null,
  allowComment: false,
  allowDuet: false,
  allowStitch: false,
  brandOrganic: false,
  brandedContent: false,
  aiGenerated: false,
};

export const YOUTUBE_OPTIONS_PADRAO: YouTubeOptions = { title: "", privacy: "public", madeForKids: null };

export const TRIAL_GRADUATION_LABELS: Record<TrialGraduation, string> = {
  MANUAL: "Eu decido no app do Instagram se compartilho com os seguidores",
  SS_PERFORMANCE: "Compartilhar com os seguidores sozinho se for bem",
};

type Limites = { minMs?: number; maxMs?: number };

/** Duração de vídeo aceita, por rede e formato. */
export const VIDEO_DURATION: Record<SocialNetwork, Record<SocialFormat, Limites>> = {
  instagram: {
    // Item de carrossel: até 60 s. Vídeo solto no feed vira Reel (a Meta
    // aposentou o VIDEO no feed), e herda o limite do Reel.
    feed: { minMs: 3_000, maxMs: 60_000 },
    reel: { minMs: 3_000, maxMs: 15 * 60_000 },
    story: { minMs: 3_000, maxMs: 60_000 },
  },
  facebook: {
    feed: { minMs: 1_000, maxMs: 4 * 60 * 60_000 },
    reel: { minMs: 3_000, maxMs: 90_000 },
    story: { minMs: 3_000, maxMs: 60_000 },
  },
  // Teto da API para apps de terceiros; a conta pode ter limite menor
  // (`max_video_post_duration_sec`), conferido na hora de publicar.
  tiktok: {
    feed: { minMs: 3_000, maxMs: 10 * 60_000 },
    reel: { minMs: 3_000, maxMs: 10 * 60_000 },
    story: {},
  },
  youtube: {
    feed: { minMs: 1_000, maxMs: 12 * 60 * 60_000 },
    reel: { minMs: 1_000, maxMs: YOUTUBE_SHORT_MAX_MS },
    story: {},
  },
  linkedin: {
    feed: { minMs: 3_000, maxMs: 30 * 60_000 },
    reel: { minMs: 3_000, maxMs: 30 * 60_000 },
    story: {},
  },
};

export type PostDraft = {
  format: SocialFormat;
  caption: string;
  media: SocialMediaInput[];
  networks: SocialNetwork[];
  trialGraduation?: TrialGraduation | null;
  scheduledAt?: Date | null;
  networkOptions?: NetworkOptions | null;
  /** O post leva um link de captação (a legenda precisa ter `{link}`). */
  temLinkDeCaptacao?: boolean;
  /** Primeiro comentário (Instagram) — também pode levar o `{link}`. */
  firstComment?: string | null;
  /** O que depende da configuração do servidor. */
  capacidades?: { tiktokFotos?: boolean };
  /** Tamanho real do link curto neste servidor (depende do domínio). */
  tamanhoDoLink?: number;
};

/** A legenda como vai sair, com o link curto no lugar do marcador. */
export function legendaComLink(caption: string, url: string | null): string {
  return caption.split(LINK_PLACEHOLDER).join(url ?? "");
}

/** Tamanho em bytes UTF-8 (o YouTube conta a descrição em bytes, não em letras). */
export function bytesUtf8(texto: string): number {
  return new TextEncoder().encode(texto).length;
}

/** Corta em até `max` bytes UTF-8 sem partir um caractere no meio. */
export function cortarEmBytes(texto: string, max: number): string {
  if (bytesUtf8(texto) <= max) return texto;
  let out = "";
  let total = 0;
  for (const ch of texto) {
    const n = bytesUtf8(ch);
    if (total + n > max) break;
    out += ch;
    total += n;
  }
  return out;
}

/** Tamanho da legenda contando o link curto que vai entrar no marcador. */
function tamanhoEfetivo(caption: string, tamanhoDoLink = TAMANHO_DO_LINK): number {
  return legendaComLink(caption, "x".repeat(tamanhoDoLink)).length;
}

/**
 * Texto no formato "little" do LinkedIn, em que `\ | { } @ [ ] ( ) < > * _ ~ #`
 * são reservados — sem escapar, o LinkedIn CORTA o texto no primeiro
 * parêntese. `#` que abre hashtag (início do texto ou depois de espaço,
 * seguido de letra ou número) fica como está; o resto (`#` de âncora numa URL,
 * `#` solto) é escapado.
 */
export function escaparLittle(texto: string): string {
  return texto.replace(/[\\|{}@[\]()<>*_~#]/g, (c, i: number) => {
    if (c === "#" && (i === 0 || /\s/.test(texto[i - 1])) && /[\p{L}\p{N}]/u.test(texto[i + 1] ?? "")) return c;
    return `\\${c}`;
  });
}

/** Hashtags contadas como o Instagram conta: `#` seguido de letra/número. */
export function contarHashtags(texto: string): number {
  return (texto.match(/#[\p{L}\p{N}_]+/gu) ?? []).length;
}

function segundos(ms: number): string {
  if (ms % 60_000 === 0 && ms >= 60_000) return `${ms / 60_000} min`;
  return `${Math.round(ms / 1000)} s`;
}

/**
 * Problemas que impedem a publicação, em português, prontos para a tela.
 * Lista vazia = pode agendar. `agora` é parâmetro para teste.
 */
export function validarPost(p: PostDraft, agora: Date = new Date()): string[] {
  const erros: string[] = [];
  const redes = [...new Set(p.networks)];
  if (redes.length === 0) erros.push("Escolha ao menos uma conta para publicar.");
  const tem = (r: SocialNetwork) => redes.includes(r);

  const imagens = p.media.filter((m) => m.kind === "image");
  const videos = p.media.filter((m) => m.kind === "video");
  const umVideoSo = p.media.length === 1 && videos.length === 1;

  for (const m of p.media) {
    if (m.kind === "image" && !(IMAGE_MIME_TYPES as readonly string[]).includes(m.mimeType)) {
      erros.push("As imagens precisam estar em JPEG (o Instagram não aceita PNG ou WEBP).");
      break;
    }
    if (m.kind === "video" && !(VIDEO_MIME_TYPES as readonly string[]).includes(m.mimeType)) {
      erros.push("Os vídeos precisam ser MP4 ou MOV.");
      break;
    }
  }
  if (imagens.some((m) => m.sizeBytes > MAX_IMAGE_BYTES)) {
    erros.push("Cada imagem pode ter até 8 MB.");
  }
  if (videos.some((m) => m.sizeBytes > MAX_VIDEO_BYTES)) {
    erros.push("Cada vídeo pode ter até 300 MB.");
  }
  if (p.media.length > MAX_MEDIA_ITEMS) {
    erros.push(`Um post tem no máximo ${MAX_MEDIA_ITEMS} mídias.`);
  }

  // Formato que a rede não tem (story fora da Meta, por exemplo).
  for (const rede of redes) {
    if (!FORMATOS_POR_REDE[rede].includes(p.format)) {
      erros.push(`${NETWORK_LABELS[rede]} não publica ${FORMAT_LABELS[p.format].toLowerCase()} pela integração — tire a conta desta publicação ou troque o formato.`);
    }
  }

  switch (p.format) {
    case "feed": {
      if (p.media.length === 0 && tem("instagram")) {
        erros.push("Post no Instagram precisa de ao menos uma imagem ou vídeo.");
      }
      if (p.media.length > CAROUSEL_MAX_ITEMS && (tem("instagram") || tem("facebook"))) {
        erros.push(`Um carrossel tem no máximo ${CAROUSEL_MAX_ITEMS} itens no Instagram e no Facebook.`);
      }
      if (tem("facebook") && videos.length > 0 && p.media.length > 1) {
        erros.push("No Facebook, vídeo vai sozinho no post — não dá para misturar com outras mídias.");
      }
      if (tem("facebook") && p.media.length === 0 && !p.caption.trim()) {
        erros.push("Post no Facebook sem mídia precisa de texto.");
      }
      // Vídeo sozinho vira Reel (qualquer proporção); o resto vai para o feed.
      if (tem("instagram") && !umVideoSo) {
        const fora = p.media.some((m) => {
          if (!m.width || !m.height) return false;
          const r = m.width / m.height;
          return r < IG_FEED_PROPORCAO_MIN - 0.01 || r > IG_FEED_PROPORCAO_MAX + 0.01;
        });
        if (fora) {
          erros.push(
            "O feed do Instagram aceita mídia de 4:5 (retrato) a 1,91:1 (paisagem). Recorte a imagem, ou publique como Story.",
          );
        }
      }
      break;
    }
    case "reel": {
      if (!umVideoSo) erros.push("Reel é um vídeo só.");
      break;
    }
    case "story": {
      if (p.media.length !== 1) erros.push("Story é uma imagem ou um vídeo só.");
      break;
    }
  }

  // TikTok: um vídeo, ou só fotos (até 35). Foto depende do domínio verificado.
  if (tem("tiktok")) {
    if (p.media.length === 0) erros.push("Post no TikTok precisa de um vídeo ou de fotos.");
    else if (videos.length > 0 && !umVideoSo) {
      erros.push("No TikTok, vídeo vai sozinho — não dá para misturar com fotos nem mandar dois vídeos.");
    } else if (videos.length === 0) {
      if (p.format !== "feed") erros.push("No TikTok, Reel precisa ser um vídeo.");
      if (imagens.length > TIKTOK_PHOTO_MAX) erros.push(`O TikTok aceita até ${TIKTOK_PHOTO_MAX} fotos por post.`);
      if (!p.capacidades?.tiktokFotos) {
        erros.push("Post de fotos no TikTok ainda não está liberado neste servidor — publique um vídeo.");
      }
    }
    if (tamanhoEfetivo(p.caption, p.tamanhoDoLink) > TIKTOK_CAPTION_MAX) {
      erros.push(`A legenda do TikTok vai até ${TIKTOK_CAPTION_MAX.toLocaleString("pt-BR")} caracteres.`);
    }
    const tk = p.networkOptions?.tiktok;
    if (!tk?.privacyLevel) erros.push("Escolha quem pode ver o vídeo no TikTok.");
    if (tk?.brandedContent && tk.privacyLevel === "SELF_ONLY") {
      erros.push("Conteúdo de marca no TikTok não pode ficar visível só para você.");
    }
  }

  // YouTube: sempre um vídeo, com título.
  if (tem("youtube")) {
    if (!umVideoSo) erros.push("No YouTube a publicação é um vídeo só.");
    const yt = p.networkOptions?.youtube;
    const titulo = yt?.title?.trim() ?? "";
    if (!titulo) erros.push("Dê um título ao vídeo do YouTube.");
    else if (titulo.length > YOUTUBE_TITLE_MAX) erros.push(`O título do YouTube vai até ${YOUTUBE_TITLE_MAX} caracteres.`);
    if (/[<>]/.test(titulo)) erros.push("O título do YouTube não aceita os sinais < e >.");
    if (/[<>]/.test(p.caption)) erros.push("A descrição do YouTube não aceita os sinais < e >.");
    if (yt?.madeForKids == null) erros.push("Responda se o vídeo do YouTube é conteúdo feito para crianças.");
    // O limite do YouTube é em BYTES: acento e emoji contam mais de um.
    if (bytesUtf8(legendaComLink(p.caption, "x".repeat(p.tamanhoDoLink ?? TAMANHO_DO_LINK))) > YOUTUBE_DESCRIPTION_MAX) {
      erros.push("A descrição passa do limite do YouTube (5.000 bytes — acentos e emojis contam mais de um).");
    }
  }

  // LinkedIn: texto, uma imagem, várias imagens (2–20) ou um vídeo.
  if (tem("linkedin")) {
    if (videos.length > 0 && !umVideoSo) {
      erros.push("No LinkedIn, vídeo vai sozinho no post — não dá para misturar com imagens.");
    }
    if (imagens.length > LINKEDIN_MULTI_IMAGE_MAX) {
      erros.push(`O LinkedIn aceita até ${LINKEDIN_MULTI_IMAGE_MAX} imagens por post.`);
    }
    if (p.media.length === 0 && !p.caption.trim()) erros.push("Post no LinkedIn sem mídia precisa de texto.");
    // O LinkedIn conta o texto JÁ escapado (cada reservado vira dois caracteres).
    if (escaparLittle(legendaComLink(p.caption, "x".repeat(p.tamanhoDoLink ?? TAMANHO_DO_LINK))).length > LINKEDIN_COMMENTARY_MAX) {
      erros.push(`O texto do LinkedIn vai até ${LINKEDIN_COMMENTARY_MAX.toLocaleString("pt-BR")} caracteres.`);
    }
  }

  // Duração: confere contra cada rede escolhida. No feed, o limite de vídeo
  // do Instagram só vale para carrossel; vídeo sozinho vira Reel.
  for (const v of videos) {
    if (v.durationMs == null) continue;
    for (const rede of redes) {
      if (!FORMATOS_POR_REDE[rede].includes(p.format)) continue;
      const formatoEfetivo: SocialFormat =
        rede === "instagram" && p.format === "feed" && p.media.length === 1 ? "reel" : p.format;
      const lim = VIDEO_DURATION[rede][formatoEfetivo];
      const rotulo = rede === "youtube" && formatoEfetivo === "reel" ? "Short" : FORMAT_LABELS[formatoEfetivo].toLowerCase();
      if (lim.minMs != null && v.durationMs < lim.minMs) {
        erros.push(`O vídeo é curto demais para ${NETWORK_LABELS[rede]} (mínimo ${segundos(lim.minMs)}).`);
      }
      if (lim.maxMs != null && v.durationMs > lim.maxMs) {
        erros.push(`O vídeo passa do limite de ${segundos(lim.maxMs)} para ${rotulo} no ${NETWORK_LABELS[rede]}.`);
      }
    }
  }

  if (p.trialGraduation) {
    if (p.format !== "reel") erros.push("Reel de teste só existe para Reels.");
    if (redes.some((r) => r !== "instagram")) {
      erros.push("Reel de teste só existe no Instagram — deixe só contas do Instagram nesta publicação ou desligue o teste.");
    }
  }

  if (tem("instagram") && p.format !== "story") {
    if (tamanhoEfetivo(p.caption, p.tamanhoDoLink) > IG_CAPTION_MAX) {
      erros.push(`A legenda do Instagram vai até ${IG_CAPTION_MAX.toLocaleString("pt-BR")} caracteres.`);
    }
    if (contarHashtags(p.caption) > IG_MAX_HASHTAGS) {
      erros.push(`O Instagram aceita até ${IG_MAX_HASHTAGS} hashtags por post.`);
    }
  }
  if (tem("facebook") && tamanhoEfetivo(p.caption, p.tamanhoDoLink) > FB_MESSAGE_MAX) {
    erros.push("O texto passa do limite do Facebook.");
  }

  // O primeiro comentário só sai no Instagram (e não em story).
  const comentarioSai = tem("instagram") && p.format !== "story";
  const noComentario = comentarioSai && (p.firstComment ?? "").includes(LINK_PLACEHOLDER);
  const marcadorEscrito = p.caption.includes(LINK_PLACEHOLDER) || (p.firstComment ?? "").includes(LINK_PLACEHOLDER);
  if (!p.temLinkDeCaptacao && marcadorEscrito) {
    erros.push(`O texto tem ${LINK_PLACEHOLDER}, mas nenhum link de captação foi escolhido.`);
  }
  if (p.temLinkDeCaptacao) {
    if (p.format === "story") {
      erros.push("Story não tem legenda para levar o link de captação — use um post ou um Reel.");
    } else if (!p.caption.includes(LINK_PLACEHOLDER) && !noComentario) {
      erros.push(
        `Coloque ${LINK_PLACEHOLDER} na legenda${comentarioSai ? " ou no primeiro comentário" : ""}, no ponto onde o link de captação deve aparecer.`,
      );
    }
  }

  if (p.scheduledAt) {
    const delta = p.scheduledAt.getTime() - agora.getTime();
    if (delta < MIN_SCHEDULE_LEAD_MS) erros.push("Escolha um horário no futuro (ou publique agora).");
    if (delta > MAX_SCHEDULE_AHEAD_MS) erros.push("Dá para agendar até 180 dias à frente.");
  }

  return [...new Set(erros)];
}

/**
 * Status do post a partir dos destinos. Um post vai para várias contas e cada
 * uma passa ou falha sozinha — o post só é "publicado" quando todas foram.
 */
export function statusDoPost(alvos: TargetStatus[], rascunho = false): PostStatus {
  if (rascunho) return "draft";
  if (alvos.length === 0) return "canceled";
  const vivos = alvos.filter((s) => s !== "canceled");
  if (vivos.length === 0) return "canceled";
  if (vivos.some((s) => s === "publishing")) return "publishing";
  if (vivos.every((s) => s === "published")) return "published";
  if (vivos.every((s) => s === "scheduled")) return "scheduled";
  if (vivos.every((s) => s === "failed")) return "failed";
  if (vivos.some((s) => s === "published") && vivos.some((s) => s === "failed")) return "partial";
  // Misto de agendado com publicado/falho: ainda há trabalho pela frente.
  return "scheduled";
}

/**
 * Marca das falhas em que NÃO dá para saber se o post saiu (passo final sem
 * resposta, execução interrompida). Todas as mensagens desse tipo contêm este
 * trecho — é por ele que se sabe que o post pode estar no ar.
 */
export const AVISO_DE_PUBLICACAO_INCERTA = "Confira no perfil";

export const POST_STATUS_LABELS: Record<PostStatus, string> = {
  draft: "Rascunho",
  scheduled: "Agendado",
  publishing: "Publicando",
  published: "Publicado",
  partial: "Publicado em parte",
  failed: "Falhou",
  canceled: "Cancelado",
};
